import * as React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { useTheme } from '@veneer/theme';
import hideVisually from 'polished/lib/mixins/hideVisually';

type Styles = {
  label: {
    base: SerializedStyles;
    required: SerializedStyles;
    disabled: SerializedStyles;
    truncate: SerializedStyles;
    counter: SerializedStyles;
  };
  insideLabel: {
    base: SerializedStyles;
    shrink: SerializedStyles;
  };
  separateLabel: {
    base: SerializedStyles;
    wrap: SerializedStyles;
  };
  counter: {
    a11y: SerializedStyles;
    scroll: SerializedStyles;
  };
};

const useStyles = ({ element }: { element: string }): Styles => {
  const theme = useTheme();
  return React.useMemo(() => {
    const area = element === 'textarea';
    const shrinkStyle = {
      ...theme.typography.labelSmall,
      top: area ? '6px' : '4px',

      [`+ ${element}`]: {
        paddingBlockStart: area ? `21px` : `calc(${theme.orbitalFive} - 1px)`,
        paddingBlockEnd: `calc(${theme.orbitalOne} - 1px)`,
      },
      // Only used because of safari height issue
      [`+ .vn-input__leading-area`]: {
        paddingBlockStart: area ? `21px` : `calc(${theme.orbitalFive} - 1px)`,
        paddingBlockEnd: `calc(${theme.orbitalOne} - 1px)`,
      },
    };

    return {
      label: {
        base: css({
          ...theme.typography.labelRegular,
          color: theme.color.foreground.medium,
          cursor: 'text',
          whiteSpace: 'nowrap',
          width: 'fit-content',
        }),
        required: css({
          '> span::after': {
            content: '"*" / ""',
            marginLeft: theme.layout.size1,
          },
        }),
        truncate: css({
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }),
        disabled: css({
          color: theme.color.foreground.disabled,
        }),
        counter: css({
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
        }),
      },
      separateLabel: {
        base: css({
          maxWidth: '100%',
        }),
        wrap: css({
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        }),
      },
      insideLabel: {
        base: css({
          zIndex: 1,
          transition:
            'font-family 150ms ease, font-size 150ms ease, font-weight 150ms ease, line-height 150ms ease,  top 150ms ease',
          lineHeight: '24px',
          [`@media (min-width: ${theme.layout.smMin})`]: { lineHeight: '24px' },
          [`@media (min-width: ${theme.layout.lgMin})`]: { lineHeight: '24px' },

          position: 'absolute',
          maxWidth: `100% `,
          overflow: 'hidden',
          textOverflow: 'ellipsis',

          alignSelf: 'flex-start',
          top: theme.orbitalThree,

          [`+ ${element}::placeholder`]: { opacity: 0 },
          [`+ ${element}:disabled::placeholder`]: { opacity: 0 },

          // only textbox/area and not select
          ...(element !== 'div' && {
            [`:not(:has(+ ${element}:placeholder-shown)), &:has(+ ${element}:focus)`]:
              shrinkStyle,
          }),
        }),
        shrink: css({
          ...shrinkStyle,
          [`+ ${element}::placeholder`]: { opacity: 1 },
        }),
        pill: css({
          left: theme.layout.size4,
          maxWidth: `calc(100% - ${theme.orbitalThree} - ${theme.orbitalFour})`,
        }),
      },
      counter: {
        a11y: css({
          ...hideVisually(),
        }),
        scroll: css({
          paddingRight: `calc(${theme.layout.size4} - 2px)`,
        }),
      },
    };
  }, [element, theme]);
};

export default useStyles;
